import React, { useEffect, useState } from "react";
import HTMLReactParser from "html-react-parser";
import { useSelector } from "react-redux";
import SubMenu from "../../../Menu/SubMenu";
import Pager from "../../../Common/Pager";
import { getData } from "../../../../lib/commonFunctions";
import HintedInput from "../../../Common/HintedInput";
import { CONTENT_CONTEXT } from "../../../../lib/enums";
import { DragescentState } from "../../../../store/reducers/stateType";
import AllianceInfoLink from "../../../Common/AllianceInfoLink";
import GovernorateInfoLink from "../../../Common/GovernorateInfoLink";

const DISPLAY_ROWS = 10;

interface IProps {
  statsType: CONTENT_CONTEXT;
  presetSearch?: string;
}

type StatsHeader = {
  name: string;
  key: string;
  type: string;
  sortable: boolean;
}

type StatsData = {
  rows: { [keys: string]: any }[];
  headers: StatsHeader[];
}

const Statistics = ({
  statsType,
  presetSearch,
}: IProps) => {
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("DESC");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<StatsData | null>(null);
  const [displayRows, setDisplayRows] = useState<{ [keys: string]: any }[]>([]);
  const [searchName, setSearchName] = useState<string | null>(presetSearch || null);
  const [searchSet, setSearchSet] = useState<boolean>(!!presetSearch);

  useEffect(() => {
    setData(null);
    setDisplayRows([]);
    setPage(1);
    getData(`/api/statistics/${statsType || CONTENT_CONTEXT.Alliance}`)
      .then((overviewData) => {
        setData(overviewData);
        setDisplayRows(overviewData.rows.slice(0, DISPLAY_ROWS));
        if (!presetSearch) {
          setSearchName(null);
          setSearchSet(false);
        }
        handleSetSearch(overviewData);
        handleSetSort("powerPoints", overviewData, true);
      });
  }, [statsType]);

  const govName = useSelector((state: DragescentState) => state.governorate.name);
  const alliance = useSelector((state: DragescentState) => state.governorate.alliance);

  const handleSetPage = (newPage: number, presetData?: StatsData) => {
    setPage(newPage);
    if (presetData || data) {
      setDisplayRows(presetData ? presetData.rows
        .slice(DISPLAY_ROWS * (newPage - 1), DISPLAY_ROWS * (newPage - 1) + DISPLAY_ROWS)
        : data ? data.rows.slice(
          DISPLAY_ROWS * (newPage - 1), DISPLAY_ROWS * (newPage - 1) + DISPLAY_ROWS
        )
          : []);
    }
  }

  const handleSetSort = (column: string, presetData?: StatsData, persistOrder?: boolean) => {
    let order = sortOrder;

    if (!persistOrder && column === sortColumn) {
      order = sortOrder === "ASC" ? "DESC" : "ASC";
    } else {
      setSortColumn(column);
    }
    const sortData = presetData || data;

    if (sortData) {
      const sortColumnTemplate = sortData.headers.find(header => header.key === column);
      if (sortColumnTemplate) {
        const { rows } = sortData;
        if (rows.length > 1) {
          rows.sort((row1, row2): any => {
            if (!row1 || !row2) {
              return 0;
            }
            if (sortColumnTemplate.type === "number") {
              return order === "ASC"
                ? row1[sortColumnTemplate.key] - row2[sortColumnTemplate.key]
                : row2[sortColumnTemplate.key] - row1[sortColumnTemplate.key]
            }
            if (sortColumnTemplate.type === "string") {
              return row1[sortColumnTemplate.key].localeCompare(row2[sortColumnTemplate.key])
                * (order === "ASC" ? -1 : 1);
            }
            if (["governorateInfo", "allianceInfo"].includes(sortColumnTemplate.type)) {
              return row1[sortColumnTemplate.key].name
                .localeCompare(row2[sortColumnTemplate.key].name)
                * (order === "ASC" ? -1 : 1);
            }

            return 0;
          })
        }

        setSortOrder(order);
        setData({ ...sortData, rows });
        setPage(1);
        setDisplayRows(rows
          .slice(0, DISPLAY_ROWS))
      }
    }
  }

  const handleSetSearch = (presetData?: StatsData) => {
    if (!searchName || (!presetData && !data)) {
      setSearchName(null);
      setSearchSet(false);
      setPage(1);
      return;
    }
    const nameIndex = presetData
      ? presetData.rows.findIndex(row => row.name.name === searchName)
      : data ? data.rows.findIndex(row => row.name.name === searchName) : -1;

    if (nameIndex !== -1) {
      handleSetPage(Math.floor(nameIndex / DISPLAY_ROWS) + 1, presetData);
      setSearchSet(true);
    }
  }

  const handleSearchOwnName = () => {
    if (!alliance && statsType !== CONTENT_CONTEXT.Governorate) {
      return;
    }
    setSearchName(statsType === CONTENT_CONTEXT.Governorate ? govName : alliance.name);
    const nameIndex = data ? data.rows.findIndex(row => row.name.name === searchName) : -1;

    if (nameIndex !== -1) {
      handleSetPage(Math.floor(nameIndex / DISPLAY_ROWS) + 1);
      setSearchSet(true);
    }
  }

  return (
    <>
       <SubMenu
        tabs={[
          {
            name: "Statistics",
            icon: "statistiky",
            title: "Statistics",
            label: "Alliances",
            active: !statsType || statsType === CONTENT_CONTEXT.Alliance,
            parameters: {
              reload: statsType && statsType !== CONTENT_CONTEXT.Alliance,
              statsType: CONTENT_CONTEXT.Alliance
            }
          },
          {
            name: "Statistics",
            icon: "statistiky",
            title: "Statistics",
            label: "Governorates",
            active: statsType === CONTENT_CONTEXT.Governorate,
            parameters: {
              reload: statsType !== CONTENT_CONTEXT.Governorate,
              statsType: CONTENT_CONTEXT.Governorate
            }
          },
        ]}
       />

      <section className="boxes-group">
        <div className="box oneline">
          <div className="row">
            <div className="col-sm-4 col-xs-8">
                <span className="input-addon">
                  <HintedInput
                    value={searchName || ""}
                    setValue={setSearchName}
                    searchArea={statsType || CONTENT_CONTEXT.Alliance}
                    className={`clearable ${searchName ? "x" : ""}`}
                  />
                {/* <input type="input" size={30} */}
                {/*       className="input js-typeahead clearable
                {{ searchName is defined ? 'x':'' }}" */}
                {/*       hint-name="hintAlliName" name="searchName"
                value="{{ searchName is defined ? searchName : '' }}" */}
                {/*       placeholder="Find alliance by name ..." */}
                {/*       data-source="/typeahead" accept-charset="UTF-8"/> */}
                <button type="submit" className="btn btn-addon" onClick={() => handleSetSearch()}>
                    <i className="icon icon-lupa"/>
                </button>
            </span>
            </div>
            <div className="col-sm-3">
              <a
                className={`btn btn-blank btn-no-width 
                ${(!statsType || statsType === CONTENT_CONTEXT.Alliance) && !alliance ? "disabled" : ""}`}
                data-scroll-target="#scroll-line"
                onClick={handleSearchOwnName}
              >
                My {statsType === CONTENT_CONTEXT.Governorate
                ? <>gov<span className={"hidden-xs"}>ernorate</span></>
                : <>alli<span className={"hidden-xs"}>ance</span></>}
              </a>
            </div>
          </div>
        </div>

        <span className="divider"/>
      </section>
      {data
      && <>
        {data.rows.length > DISPLAY_ROWS
        && <Pager pages={Math.ceil(data.rows.length / DISPLAY_ROWS)} page={page}
                bottom={false} setPage={handleSetPage}/>}
        <span className="divider"/>
        <div className="box scrollable">
          <table className="table table-hover">
            <thead>
            <tr>
              <th>#</th>
              {data.headers.map(header => (
                <th
                  className={`${sortColumn === header.key
                    ? `sorted ${sortOrder.toLowerCase()}` : ""} ${["string", "governorateInfo", "allianceInfo"]
                    .includes(header.type) ? "text-left" : ""}`}
                >
                  {header.sortable
                    ? <a
                      href="#"
                      onClick={() => handleSetSort(header.key)}
                    >
                      {HTMLReactParser(header.name)}
                    </a> : <>{header.name}</>
                  }
                </th>))
              }
            </tr>
            </thead>
            <tbody>
            {displayRows.map((row, index) => (
                <tr className={`${searchSet && row.name && row.name.name === searchName ? "active" : ""}`}>
                  <td className={"text-center"}>{((page - 1) * DISPLAY_ROWS) + index + 1}</td>
                  {data.headers.map(column => row[column.key] !== undefined
                    && (<td className={`${column.type === "number" ? "text-center" : ""}`}>
                      {column.type === "allianceInfo"
                        ? (row[column.key].id ? <AllianceInfoLink
                          allianceId={row[column.key].id}
                          name={row[column.key].name}
                          classes={row.emperor?.warStatus && "negative"}
                        /> : row[column.key].name)
                        : column.type === "governorateInfo"
                          ? (row[column.key].id ? <GovernorateInfoLink
                            govId={row[column.key].id}
                            name={row[column.key].name}
                            raceName={""}
                          /> : row[column.key].name)
                          : <>{row[column.key]}</>
                      }
                    </td>))}
                </tr>))
            }
            </tbody>
          </table>
        </div>

        <span className="divider"/>
        {data.rows.length > DISPLAY_ROWS
        && <Pager pages={Math.ceil(data.rows.length / DISPLAY_ROWS)} page={page}
                bottom={true} setPage={handleSetPage}/>}
      </>}
    </>
  )
};

export default Statistics;
